import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import type { StateAction } from "@entrilia/shared/types";
import type { SubmissionAttempt } from "@entrilia/shared/utilities/submissionAttempt";
import { resolveSubmissionAttempt } from "@entrilia/shared/utilities/submissionAttempt";
import { api } from "../../../../api/client";
import type { FundInvestorContact } from "../../../../api/types/investorContactsTypes";
import type { MembersChangeRequestView } from "../../../../api/types/membersChangeRequestTypes";
import useClientContext from "../../../../contexts/useClientContext";
import useImpersonationBlockContext from "../../../../contexts/useImpersonationBlockContext";
import type { InvestorFundPermissionSet } from "../../../../store/state/user/types";
import type { ContactChangesState } from "./contactChangesState";
import {
  changesToDeltaRequest,
  stateToUpsertMembersChangeRequestRequest,
  updateCommentAction,
} from "./contactChangesState";
import FinalRequestSection from "./memberChanges/FinalRequestSection";
import MemberChangesSections from "./memberChanges/MemberChangesSections";
import {
  CHANGE_REQUEST_SUBMITTED_MESSAGE,
  CHANGE_REQUEST_WITHDRAWN_MESSAGE,
  getNewChangesView,
  resolveDisplayedComment,
  resolveSubmittedComment,
} from "./membersChangeRequestHelper";

interface Props {
  onClose: () => void;
  investorId: string;
  changesState: ContactChangesState;
  setChangesState: (action: StateAction<ContactChangesState>) => void;
  contacts: FundInvestorContact[];
  fundInvestors: InvestorFundPermissionSet[];
  latestChangeRequest: MembersChangeRequestView | undefined;
  onDiscardChanges: () => void;
  onSubmitted: (changeRequest: MembersChangeRequestView) => void;
}

const upsertMembersChangeRequest = withErrorHandling(api.membersChangeRequests.upsert);

const ReviewChangesDialog = ({
  onClose,
  investorId,
  changesState,
  setChangesState,
  contacts,
  fundInvestors,
  latestChangeRequest,
  onDiscardChanges,
  onSubmitted,
}: Props) => {
  const { portalSettings } = useClientContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { isBlockedUnderImpersonation, showImpersonationBlockedDialog } = useImpersonationBlockContext();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionAttempt, setSubmissionAttempt] = useState<SubmissionAttempt>();

  // Destructured so that comment edits don't recreate the callback and refetch the preview
  const { newContacts, removedContactIds, contactUpdates, revertedChangeItemIds } = changesState;

  const getPreview = useCallback(
    () =>
      api.membersChangeRequests.preview(
        investorId,
        changesToDeltaRequest(
          { newContacts, removedContactIds, contactUpdates, revertedChangeItemIds },
          contacts,
          fundInvestors,
          latestChangeRequest
        )
      ),
    [
      investorId,
      newContacts,
      removedContactIds,
      contactUpdates,
      revertedChangeItemIds,
      contacts,
      fundInvestors,
      latestChangeRequest,
    ]
  );
  const [preview, previewError] = useFetch(getPreview);

  const supportContact = portalSettings?.supportEmail ?? "the support";

  const handleSubmit = async () => {
    setIsSubmitting(true);
    // Signature covers the submitted content and base etag, so a retry reuses this id and an edit gets a new one
    const attempt = resolveSubmissionAttempt([changesState, latestChangeRequest?.etag], submissionAttempt);
    setSubmissionAttempt(attempt);
    const payload = stateToUpsertMembersChangeRequestRequest(
      changesState,
      latestChangeRequest,
      contacts,
      fundInvestors,
      attempt.id
    );
    const [updatedChangeRequest, error] = await upsertMembersChangeRequest(investorId, payload);
    setIsSubmitting(false);

    if (error) {
      if (isBlockedUnderImpersonation(error)) {
        showImpersonationBlockedDialog();
        return;
      }
      logError(error, "[ReviewChangesDialog] upsertMembersChangeRequest");
      sendNotificationError(error.message);
      return;
    }

    sendNotification(cancelsPendingRequest ? CHANGE_REQUEST_WITHDRAWN_MESSAGE : CHANGE_REQUEST_SUBMITTED_MESSAGE);
    onSubmitted(updatedChangeRequest);
  };

  if (previewError) {
    logError(previewError, "[ReviewChangesDialog] previewMembersChangeRequest");
  }

  const pendingChangeRequest = latestChangeRequest?.changeRequestId ? latestChangeRequest : undefined;
  const contactsCount = preview?.items.length ?? 0;
  // All pending changes reverted: the merged request is empty, so submitting cancels it instead of updating it
  const cancelsPendingRequest = contactsCount === 0 && !!pendingChangeRequest;
  // Amending a pending request: only the changes made since it was submitted are new, the merged request is shown separately
  const newChanges = preview && pendingChangeRequest ? getNewChangesView(preview, pendingChangeRequest) : undefined;
  const comment = resolveDisplayedComment(changesState.comment, latestChangeRequest) ?? "";
  // The preview is requested without a comment, so the panel has to follow the submission, not the pending request
  const submittedComment = resolveSubmittedComment(changesState.comment, latestChangeRequest);

  return (
    <Dialog open maxWidth="md" fullWidth slotProps={{ paper: { sx: { height: "80vh" } } }}>
      <DialogTitle>Review Changes</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <Divider />
      <DialogContent sx={{ display: "flex", flexDirection: "column" }}>
        {previewError ? (
          <DataLoadingFailed title="Failed to load the changes preview" text={previewError.message} />
        ) : preview === undefined ? (
          <InlineLoader />
        ) : (
          <Stack spacing={2.5}>
            <Alert severity="warning" icon={false} sx={{ ".MuiAlert-message": { width: "100%" } }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                <Typography>
                  {cancelsPendingRequest ? (
                    <>
                      You&apos;ve reverted all your pending changes. Submitting will <strong>withdraw them</strong>
                      {pendingChangeRequest?.referenceCode
                        ? ` from request #${pendingChangeRequest.referenceCode}`
                        : " from the pending request"}
                      .
                    </>
                  ) : (
                    <>
                      You&apos;re about to submit changes across{" "}
                      <strong>
                        {contactsCount} {contactsCount === 1 ? "contact" : "contacts"}
                      </strong>
                      . Updating information requires approval — upon submitting, the request will be forwarded to{" "}
                      {supportContact} for review and confirmation. Any existing pending request will be updated with
                      the latest changes instead of creating a new one.
                    </>
                  )}
                </Typography>
                <Button
                  variant="outlined"
                  color="inherit"
                  sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                  onClick={onDiscardChanges}
                >
                  Discard Changes
                </Button>
              </Box>
            </Alert>
            {newChanges ? (
              <Stack spacing={2}>
                <Typography variant="subtitle1">New Changes to Review</Typography>
                {newChanges.items.length > 0 ? (
                  <Stack spacing={2.5}>
                    <MemberChangesSections changeRequest={newChanges} />
                  </Stack>
                ) : (
                  <Typography color="textSecondary">No new changes</Typography>
                )}
              </Stack>
            ) : (
              <MemberChangesSections changeRequest={preview} />
            )}
            {pendingChangeRequest && contactsCount > 0 && (
              <FinalRequestSection
                mergedChangeRequest={{ ...preview, comment: submittedComment ?? preview.comment }}
                pendingChangeRequest={pendingChangeRequest}
              />
            )}
            <Stack spacing={1.5}>
              <Typography variant="subtitle1">User Comment</Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                spellCheck
                placeholder="Add an optional comment for the reviewer..."
                variant="outlined"
                value={comment}
                onChange={(e) => setChangesState(updateCommentAction(e.target.value))}
              />
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <Divider />
      <DialogButtons>
        <Button variant="outlined" color="secondary" onClick={onClose}>
          Continue Editing
        </Button>
        <Button
          variant="contained"
          loading={isSubmitting}
          disabled={preview === undefined || !!previewError}
          onClick={handleSubmit}
        >
          {pendingChangeRequest ? "Update Request" : "Submit Request"}
        </Button>
      </DialogButtons>
    </Dialog>
  );
};

export default ReviewChangesDialog;
